/** Course cards and the course detail layout, shared by the public site and the student area. */
import { config } from '../../core/config.js';
import { html } from '../../core/html.js';
import { fmtDate, hue, inr } from '../../core/format.js';
import { av, chips, ic, tag } from '../../ui/components.js';

/** "Starts 14 Oct", "In progress", "Enrolling now" from a batch. */
export function startLabel(batch) {
  if (!batch) return 'Dates soon';
  if (!batch.starts_on) return 'Enrolling now';
  const start = new Date(batch.starts_on);
  return start > new Date() ? `Starts ${fmtDate(start, { year: undefined })}` : 'In progress';
}

function trainerLine(trainer) {
  if (!trainer) return html`<div class="sm mu">Trainer to be announced</div>`;
  const years = trainer.years_exp ? `, ${trainer.years_exp}+ years` : '';
  return html`<div class="sm"><b>${trainer.name}</b><div class="xs mu">${trainer.headline || ''}${years}</div></div>`;
}

/** @param {object[]} courses  each course may carry `trainer` and `next_batch` */
export function courseCards(courses, { base = '/course' } = {}) {
  return html`<div class="grid g3">${courses.map((c) => html`<a class="card click" href="${base}/${c.slug}">
    <div class="cov" style="--h:${hue(c.category)}"><span class="cat">${c.category}</span>${c.trainer?.rating ? html`<span class="rt">${ic('star')} ${c.trainer.rating}</span>` : ''}<i aria-hidden="true">${c.title.charAt(0)}</i></div>
    <h3 class="ct">${c.title}</h3>
    <div class="row nw mt-s">${av(c.trainer?.name || 'Trainer', 'sm')}${trainerLine(c.trainer)}</div>
    <div class="divider"></div>
    <div class="row between"><b>${c.price_paise === 0 ? 'Free' : inr(c.price_paise)}${c.price_paise === 0 ? '' : html`<span class="mu sm" style="font-weight:400"> /month</span>`}</b><span class="xs mu">${startLabel(c.next_batch)}</span></div></a>`)}</div>`;
}

/**
 * @param {object} p
 * @param {object} p.course
 * @param {object|null} p.trainer
 * @param {object[]} p.batches   open batches with seats_left
 * @param {number} p.gst         GST percent shown under the price
 * @param {object|null} p.trial  { enabled, next_at } when the course offers a free trial class
 * @param {any} p.action         the call-to-action block (join, subscribe, enrolled...)
 */
export function courseDetail({ course, trainer, batches, gst, action, trial = null }) {
  const curriculum = course.curriculum?.length ? course.curriculum : [];
  const first = batches?.[0];
  return html`<div class="cd-grid">
    <div><span class="tag n">${course.category}</span><h1 class="mt-s" style="font-size:clamp(30px,4vw,42px)">${course.title}</h1>
      <p class="mu" style="font-size:17px">${course.description}</p>
      ${trainer ? html`<div class="card flat mt-s"><div class="row nw">${av(trainer.name, 'lg')}<div><div class="b">${trainer.name}</div>
        <div class="sm mu">${trainer.headline || 'Working professional'}${trainer.company ? html` at ${trainer.company}` : ''}${trainer.years_exp ? `, ${trainer.years_exp}+ years` : ''}</div>
        ${trainer.rating ? html`<div class="sm">${ic('star')} ${trainer.rating} trainer rating</div>` : ''}</div></div></div>` : ''}
      ${course.skills?.length ? html`<h3 class="mt">What you will learn</h3>${chips(course.skills)}` : ''}
      ${curriculum.length ? html`<h3 class="mt">Curriculum</h3><div class="card flat"><div class="list">${curriculum.map((w, i) => html`<div class="li"><span class="tag">Week ${i + 1}</span><span>${w}</span></div>`)}</div></div>` : ''}
    </div>
    <div class="card" style="position:sticky;top:90px"><div class="stat"><div class="k">${course.price_paise === 0 ? 'Free' : inr(course.price_paise)}${course.price_paise === 0 ? '' : html`<span class="mu" style="font:500 15px var(--fb)"> /month</span>`}</div></div>
      <p class="sm mu">${course.price_paise === 0 ? 'No payment needed. Register to get the joining link.' : html`${gst ? `Plus ${gst}% GST. ` : ''}Cancel any time.`}</p>
      <div class="list sm">
        ${first ? html`<div class="li">${ic('cal')} ${first.schedule}</div>` : ''}
        ${first?.starts_on ? html`<div class="li">${ic('cal')} ${startLabel(first)}</div>` : ''}
        ${trial?.enabled ? html`<div class="li">${ic('play')} Free trial class available${trial.next_at ? html`. Next: ${fmtDate(trial.next_at)}` : '. Dates coming soon'}</div>` : ''}
        <div class="li">${ic('play')} ${course.total_classes} live ${course.total_classes === 1 ? 'class' : 'classes'}${config.recordings ? ', recorded' : ''}</div>
        <div class="li">${ic('layers')} Projects and weekly assignments</div>
        <div class="li">${ic('award')} Certificate after the final assessment</div>
        ${first ? html`<div class="li">${ic('users')} Batch ${first.name}: ${first.seats_left} ${first.seats_left === 1 ? 'seat' : 'seats'} left</div>` : html`<div class="li">${ic('users')} ${tag('Enrolment opens soon', 'wn')}</div>`}
      </div>${action}</div></div>`;
}
